import React, { useEffect, useRef, useState, ReactNode } from "react";
import desktop_video from "../images/bloom-v2-desktop.mp4";
import mobile_video from "../images/bloom-v1-mobile.mp4";
import { ButtonSecondary } from "./Button";
import { Title } from "./Title";
import scrollTo from "gatsby-plugin-smoothscroll";

interface ParallaxVideoProps {
  children: ReactNode;
}

export const ParallaxVideo = ({ children }: ParallaxVideoProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [aboveFold, setAboveFold] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  const handleScroll = () => scrollTo("#services-bubble");
  const videoSrc = isMobile ? mobile_video : desktop_video;

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const attemptPlay = () => {
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          videoElement.muted = true;
          videoElement.play().catch(() => undefined);
        });
      }
    };

    if (videoElement.readyState >= 2) {
      attemptPlay();
      return;
    }

    const handleCanPlay = () => {
      attemptPlay();
      videoElement.removeEventListener("canplay", handleCanPlay);
    };

    videoElement.addEventListener("canplay", handleCanPlay);

    return () => videoElement.removeEventListener("canplay", handleCanPlay);
  }, [videoSrc]);

  return (
    <div className="relative w-full">
      {/* Fixed video background */}
      <video
        key={videoSrc}
        ref={videoRef}
        className="fixed top-0 left-0 -z-10 object-cover w-screen h-screen max-h-[100vh]"
        muted
        defaultMuted
        preload="auto"
        playsInline
        loop
        autoPlay
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay header section */}
      <section
        className="relative z-10 flex flex-col items-center justify-center w-full h-screen text-center text-white bg-black/50"
      >
        <Title color="rose" className="mx-auto !mb-0 text-sandDark header !italic">
          Health & Bloom
        </Title>
        <Title color="blue" className="lead-8 lg:!leading-[40px] sub-header lg:text-[32px] !mt-2 md:!mt-4 !mb-4">
          Life Coaching, Somatic Therapy, <br />and Wellness Center.
        </Title>
        <p className="!text-thin !font-primary text-sandLight text-large lg:text-[22px] max-w-[285px] lg:max-w-[580px]">
          Where healing is an art, growth is unfolding <br className="hidden lg:block" />and every soul finds its rhythm
        </p>
        <ButtonSecondary
          link="#tools-for-liberation"
          onClick={handleScroll}
          className="mt-6 text-sandLight text-md xl:text-lg"
        >
          Learn more
          <svg
            className="align-middle relative top-[2px] -rotate-90 w-2.5 h-2.5 ml-2.5 transform -translate-x-1 group-hover:translate-x-[-2px] transition-all"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              className={`transition-colors stroke-sandLight group-hover:stroke-blueLight`}
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 1 4 4 4-4"
            />
          </svg>
        </ButtonSecondary>
      </section>

      {/* Actual scrollable content */}
      <div className="relative z-10 bg-white">
        {children}
      </div>
    </div>
  );
};
